import { TranslationFile } from 'i18n'
import { useTranslation } from 'react-i18next'

import appConfig from 'app.config'
import { IApplicationProps } from 'interfaces/i-application-props'
import {
	GetAllCitizenshipsWithTranslation,
	GetAllCountriesWithTranslation,
} from 'utils/countryHelpers'
import { formatToStringLocale } from 'utils/dateAndTimeHelpers'
import { Gender } from 'constants/enums/genderTypes'
import { enumArrayToString, FormatEnumToList } from 'utils/commonhelpers'
import { MaritalStatus } from 'constants/enums/maritalStatusTypes'
import { PropertyType } from 'constants/enums/propertyTypes'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { PermitType } from 'constants/enums/permitTypes'
import { AllCountries, euCountries } from 'constants/enums/countryTypes'
import { yearsAgo, yearsInFuture } from 'formBuilder/src'

// Es
export const ApplicationPersonalInformationForm = (data: IApplicationProps) => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
		TranslationFile.Countries,
	])

	return {
		fieldSetName: '',
		formName: 'PersonalInformationForm',
		description: t('forms:PersonalInformationDescription'),
		collapsable: false,
		show: true,
		visible: true,
		market: appConfig.country,
		fullSize: true,
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'firstName',
				type: 'text',
				label: t('forms:FirstName'),
				value: data.applicationData?.personalInfo?.firstName,
				visible: true,
				// disabled: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'lastName',
				type: 'text',
				label: t('forms:LastName'),
				value: data.applicationData?.personalInfo?.lastName,
				visible: true,
				// disabled: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'ssn',
				type: 'text',
				label: t('forms:Ssn'),
				value: data.personalData?.ssn,
				visible: true,
				disabled: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'nationality',
				type: 'dropdown',
				label: t('forms:Nationality'),
				value: data.applicationData?.personalInfo?.nationality,
				options: GetAllCountriesWithTranslation(appConfig.country),
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownCountry'),
			},
			{
				required: true,
				controlName: 'birthDate',
				type: 'text',
				label: t('forms:BirthDate'),
				value: formatToStringLocale(data.personalData?.birthDate!),
				visible: true,
				disabled: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'countryOfBirth',
				type: 'dropdown',
				label: t('forms:CountryOfBirth'),
				options: GetAllCountriesWithTranslation(appConfig.country),
				value: data.applicationData?.personalInfo?.countryOfBirth,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownCountry'),
			},
			{
				required: true,
				controlName: 'gender',
				type: 'dropdown',
				label: t('forms:Gender'),
				options: [
					{
						value: Gender.Female,
						label: t('dropdown:Female'),
					},
					{
						value: Gender.Male,
						label: t('dropdown:Male'),
					},
				],
				value:
					data.applicationData?.personalInfo?.gender ===
					Gender.Unknown
						? null
						: data.applicationData?.personalInfo?.gender,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownGender'),
			},
			{
				required: true,
				controlName: 'maritalStatus',
				type: 'dropdown',
				label: t('forms:MaritalStatus'),
				options: FormatEnumToList(
					MaritalStatus,
					PropertyType.MaritalStatus,
					TranslationFile.Dropdown,
				),
				value:
					data.applicationData?.personalInfo?.maritalStatus ===
					MaritalStatus.Unknown
						? null
						: data.applicationData?.personalInfo?.maritalStatus,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownMaritalStatus'),
			},
			{
				required: true,
				controlName: 'childrenInFamily',
				type: 'text',
				label: t('forms:ChildrenInFamily'),
				validations: [
					{
						code: 'MustMatchPattern',
						value: '\\b^([0-9]|[1-9][0-9]|100)\\b$',
						message: t('forms:ValidationMsgChildrenInFamily'),
					},
				],
				value: data.applicationData?.accommodation?.childrenInFamily,
				visible: true,
				col: 12,
			},
		],
	}
}

export const ApplicationPersonalInformationFormLt = (
	data: IApplicationInformation,
) => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
		TranslationFile.Countries,
	])

	return {
		fieldSetName: '',
		formName: 'PersonalInformationForm',
		collapsable: false,
		show: true,
		visible: true,
		market: appConfig.country,
		fullSize: true,
		col: 12,
		controls: [
			{
				controlName: 'firstName',
				type: 'text',
				label: t('forms:FirstName'),
				value: data?.personalInfo?.firstName,
				visible: true,
				disabled: true,
				col: 6,
			},
			{
				controlName: 'lastName',
				type: 'text',
				label: t('forms:LastName'),
				value: data?.personalInfo?.lastName,
				visible: true,
				disabled: true,
				col: 6,
			},
			{
				controlName: 'email',
				type: 'text',
				label: t('forms:Email'),
				value: data?.contacts?.email,
				visible: true,
				disabled: true,
				col: 6,
			},
			{
				controlName: 'cellPhone',
				type: 'text',
				label: t('forms:MobilePhone'),
				value: data?.contacts?.cellPhone,
				visible: true,
				disabled: true,
				col: 6,
			},
		],
	}
}

export const ApplicationPersonalInformationFormDe = (
	data: IApplicationInformation,
) => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
		TranslationFile.Countries,
	])
	const euCountriesList = enumArrayToString(AllCountries, euCountries, true)

	return {
		fieldSetName: '',
		formName: 'PersonalInformationForm',
		description: t('forms:PersonalInformationDescription'),
		collapsable: false,
		show: true,
		visible: true,
		market: appConfig.country,
		fullSize: true,
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'maritalStatus',
				type: 'dropdown',
				label: t('forms:MaritalStatus'),
				options: FormatEnumToList(
					MaritalStatus,
					PropertyType.MaritalStatus,
					TranslationFile.Dropdown,
				),
				value: data.personalInfo?.maritalStatus,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownMaritalStatus'),
			},
			{
				required: true,
				controlName: 'cityOfBirth',
				type: 'input',
				label: t('forms:CityOfBirth'),
				visible: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'countryOfBirth',
				type: 'dropdown',
				label: t('forms:CountryOfBirth'),
				value: data.personalInfo?.countryOfBirth,
				options: GetAllCountriesWithTranslation(appConfig.country),
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownCountry'),
			},
			{
				required: true,
				controlName: 'nationality',
				type: 'dropdown',
				label: t('forms:Nationality'),
				value: data.personalInfo?.nationality,
				options: GetAllCitizenshipsWithTranslation(appConfig.country),
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownCountry'),
			},
			{
				required: true,
				controlName: 'inCountrySince',
				type: 'datePicker',
				minDate: yearsAgo(100),
				maxDate: new Date(), // today
				label: t('forms:InCountrySinceDate'),
				visible: false,
				col: 12,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'nationality',
								rule: 'is not empty',
							},
							{
								field: 'nationality',
								rule: 'not in',
								value: euCountriesList,
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'residencePermit',
				type: 'dropdown',
				label: t('forms:ResidencePermit'),
				options: FormatEnumToList(
					PermitType,
					PropertyType.PermitType,
					TranslationFile.Dropdown,
				),
				visible: false,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownResidencePermit'),
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'nationality',
								rule: 'is not empty',
							},
							{
								field: 'nationality',
								rule: 'not in',
								value: euCountriesList,
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'residencePermitUntil',
				type: 'datePicker',
				minDate: new Date(), // today
				maxDate: yearsInFuture(100),
				label: t('forms:ResidencePermitUntil'),
				visible: false,
				col: 12,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'nationality',
								rule: 'is not empty',
							},
							{
								field: 'nationality',
								rule: 'not in',
								value: euCountriesList,
							},
							{
								field: 'residencePermit',
								rule: 'equal',
								value: 'Limited',
							},
						],
					},
				],
			},
		],
	}
}
